import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useOrg } from '../../context/OrgContext';
import { useAuth } from '../../context/AuthContext';
import { useDataContext } from '../../context/DataContext';
import { isDemoOrg } from '../../data/demoData';
import DashboardStatusIndicator from '../../features/cafe/components/DashboardStatusIndicator';

const ShopDashboard: React.FC = () => {
  const { currentOrganization } = useOrg();
  const { currentUser } = useAuth();
  const { orderLimits, refreshOrderLimits } = useDataContext();

  // Load order limits only when organization changes (cached for 5 minutes)
  useEffect(() => {
    if (currentOrganization) {
      refreshOrderLimits();
    }
  }, [currentOrganization]); // Removed refreshOrderLimits from dependencies to prevent unnecessary calls

  if (!currentOrganization) {
    return (
      <div className="loading-container">
        <div className="loading-spinner-container">
          <div className="loading-spinner-text">Loading cafe dashboard...</div>
          <div className="loading-spinner-element"></div>
        </div>
      </div>
    );
  }

  const cafeSlug = currentOrganization.urlName;
  const isDemo = isDemoOrg(cafeSlug);

  const tiles = [
    {
      title: 'Public Menu',
      description: 'Customer-facing page with menu and information',
      icon: '🌐',
      link: `/${cafeSlug}/public`,
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      enabled: true,
      category: 'public',
      accent: '#667eea'
    },
    {
      title: 'Kitchen Dashboard',
      description: 'View and manage incoming orders',
      icon: '👨‍🍳',
      link: `/${cafeSlug}/kitchen`,
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      requiresAuth: true,
      category: 'management',
      accent: '#f5576c'
    },
    {
      title: 'Menu Management',
      description: 'Add, edit, and organize menu items',
      icon: '📋',
      link: `/${cafeSlug}/menu`,
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      requiresAuth: true,
      category: 'management',
      accent: '#4facfe'
    },
    {
      title: 'Table Management',
      description: 'Manage tables and seating arrangements',
      icon: '🪑',
      link: `/${cafeSlug}/tables`,
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      requiresAuth: true,
      category: 'management',
      accent: '#43e97b'
    },
    {
      title: 'Customer Orders',
      description: 'Customer self-service ordering system',
      icon: '📱',
      link: `/${cafeSlug}/order`,
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      enabled: true,
      category: 'public',
      accent: '#fa709a'
    },
    {
      title: 'Cafe Settings',
      description: 'Configure cafe preferences and features',
      icon: '⚙️',
      link: `/${cafeSlug}/settings`,
      gradient: 'linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)',
      requiresAuth: true,
      category: 'management',
      accent: '#d299c2'
    }
  ];

  const availableTiles = tiles.filter(tile => {
    if (tile.enabled === false) return false;
    // For demo org, show all tiles regardless of authentication
    if (isDemo) return true;
    // For non-demo orgs, require authentication for management tiles
    if (tile.requiresAuth && !currentUser) return false;
    return true;
  });

  // Group tiles by category for better mobile organization
  const publicTiles = availableTiles.filter(tile => tile.category === 'public');
  const managementTiles = availableTiles.filter(tile => tile.category === 'management');

  return (
    <div className="home-container fade-in">
      {/* Enhanced Header with Apple Design */}
      <div className="home-header">
        <div className="home-title-section">
          <h1 className="home-title">{currentOrganization.name}</h1>
          <p className="home-subtitle">Cafe Management Dashboard</p>
          <div className="home-meta">
            <span className="meta-item">
              <span className="meta-icon">📍</span>
              <a 
                href={window.location.origin} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ 
                  color: 'inherit', 
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
              >
                <span className="meta-text">{window.location.origin}</span>
                <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>↗</span>
              </a>
            </span>
            <span className="meta-item">
              <span className="meta-icon">👤</span>
              <span className="meta-text">
                {isDemo ? 'Demo Mode' : (currentUser ? 'Admin Access' : 'Public Access')}
              </span>
            </span>
            {isDemo && (
              <span className="meta-item">
                <span className="meta-icon">🎯</span>
                <span className="meta-text">All features available</span>
              </span>
            )}
          </div>
        </div>
        <DashboardStatusIndicator />
      </div>

      {/* Demo Mode Notice */}
      {isDemo && (
        <div className="demo-notice">
          <div className="demo-notice-content">
            <span className="demo-notice-icon">🎯</span>
            <div className="demo-notice-text">
              <strong>Demo Mode Active</strong>
              <span>All features are available with mock data. No authentication required.</span>
            </div>
          </div>
        </div>
      )}

      {/* Order Limit Warning */}
      {orderLimits && orderLimits.remainingOrders <= 50 && (
        <div style={{ 
          margin: '1rem 0',
          padding: '1rem',
          backgroundColor: orderLimits.remainingOrders <= 10 ? '#fee' : '#fff3cd',
          border: `1px solid ${orderLimits.remainingOrders <= 10 ? '#f5c6cb' : '#ffeaa7'}`,
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '0.5rem'
          }}>
            <div style={{ 
              fontSize: '1.2rem', 
              fontWeight: 'bold', 
              color: orderLimits.remainingOrders <= 10 ? '#721c24' : '#856404'
            }}>
              ⚠️ Order Limit Warning
            </div>
            <button
              onClick={() => refreshOrderLimits(true)}
              style={{
                background: 'none',
                border: '1px solid #ccc',
                borderRadius: '4px',
                padding: '0.25rem 0.5rem',
                fontSize: '0.8rem',
                cursor: 'pointer',
                color: '#666'
              }}
              title="Refresh order limits"
            >
              🔄
            </button>
          </div>
          <div style={{ 
            fontSize: '1rem', 
            color: orderLimits.remainingOrders <= 10 ? '#721c24' : '#856404',
            marginTop: '0.5rem'
          }}>
            Only {orderLimits.remainingOrders} orders remaining today
          </div>
          <div style={{ 
            fontSize: '0.9rem', 
            color: orderLimits.remainingOrders <= 10 ? '#721c24' : '#856404',
            marginTop: '0.25rem',
            opacity: 0.8
          }}>
            {orderLimits.ordersInLast24Hours}/{orderLimits.maxOrdersPerDay} used
          </div>
          <div style={{ 
            fontSize: '0.8rem', 
            color: orderLimits.remainingOrders <= 10 ? '#721c24' : '#856404',
            marginTop: '0.5rem',
            opacity: 0.7
          }}>
            Contact: vadym@suprun.ca
          </div>
          <div style={{ 
            fontSize: '0.8rem', 
            color: orderLimits.remainingOrders <= 10 ? '#721c24' : '#856404',
            marginTop: '0.25rem',
            opacity: 0.7
          }}>
            URL: {window.location.origin}{window.location.pathname.split('/').slice(0, 2).join('/')}
          </div>
        </div>
      )}

      {/* Public Access Section */}
      {publicTiles.length > 0 && (
        <div className="dashboard-section">
          <div className="section-header">
            <h2 className="section-title">Public Access</h2>
            <p className="section-subtitle">Customer-facing features and ordering</p>
          </div>
          <div className="feature-tiles-grid">
            {publicTiles.map((tile) => (
              <Link
                key={tile.title}
                to={tile.link}
                className="feature-tile-link"
              >
                <div className="feature-tile">
                  <div 
                    className="feature-tile-gradient"
                    style={{ background: tile.gradient }}
                  ></div>
                  <div className="feature-tile-content">
                    <div className="feature-tile-icon" style={{ color: tile.accent }}>{tile.icon}</div>
                    <h3 className="feature-tile-title">{tile.title}</h3>
                    <p className="feature-tile-description">{tile.description}</p>
                  </div>
                  <div className="feature-tile-arrow">→</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Management Section */}
      {managementTiles.length > 0 && (
        <div className="dashboard-section">
          <div className="section-header">
            <h2 className="section-title">
              {isDemo ? 'Management Tools (Demo)' : 'Management Tools'}
            </h2>
            <p className="section-subtitle">
              {isDemo ? 'Admin features with demo data' : 'Admin features for cafe management'}
            </p>
          </div>
          <div className="feature-tiles-grid">
            {managementTiles.map((tile) => (
              <Link
                key={tile.title}
                to={tile.link}
                className="feature-tile-link"
              >
                <div className="feature-tile">
                  <div 
                    className="feature-tile-gradient"
                    style={{ background: tile.gradient }}
                  ></div>
                  <div className="feature-tile-content">
                    <div className="feature-tile-icon" style={{ color: tile.accent }}>{tile.icon}</div>
                    <h3 className="feature-tile-title">{tile.title}</h3>
                    <p className="feature-tile-description">{tile.description}</p>
                    {tile.requiresAuth && !isDemo && (
                      <div className="feature-tile-auth-badge">
                        🔒 Admin Access
                      </div>
                    )}
                    {isDemo && (
                      <div className="feature-tile-demo-badge">
                        🎯 Demo Mode
                      </div>
                    )}
                  </div>
                  <div className="feature-tile-arrow">→</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Enhanced Quick Info Cards */}
      <div className="quick-info-section">
        <div className="section-header">
          <h2 className="section-title">Quick Information</h2>
        </div>
        <div className="quick-info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
          <div className="info-card" style={{ 
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '1.25rem',
            transition: 'all 0.2s ease',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
            }}></div>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.75rem',
              marginBottom: '1rem'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '1.25rem'
              }}>
                🌐
              </div>
              <div style={{ flex: 1 }}>
                <h4 style={{
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  color: '#64748b',
                  margin: '0 0 0.25rem 0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Public URL
                </h4>
                <div style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  padding: '0.5rem 0.75rem',
                  fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
                  fontSize: '0.875rem',
                  color: '#1e293b',
                  wordBreak: 'break-all',
                  lineHeight: '1.4'
                }}>
                  <span className="mobile-hidden">{window.location.origin}/{cafeSlug}/public</span>
                  <span className="mobile-visible">/{cafeSlug}/public</span>
                </div>
              </div>
            </div>
            <button 
              style={{
                background: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '0.5rem 1rem',
                fontSize: '0.75rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
              onMouseEnter={(e) => {
                const btn = e.target as HTMLButtonElement;
                btn.style.background = '#5a67d8';
              }}
              onMouseLeave={(e) => {
                const btn = e.target as HTMLButtonElement;
                btn.style.background = '#667eea';
              }}
              onClick={(e) => {
                navigator.clipboard.writeText(`${window.location.origin}/${cafeSlug}/public`);
                const btn = e.target as HTMLButtonElement;
                if (btn) {
                  const originalText = btn.textContent;
                  btn.textContent = '✓ Copied!';
                  btn.style.background = '#10b981';
                  setTimeout(() => {
                    btn.textContent = originalText;
                    btn.style.background = '#667eea';
                  }, 2000);
                }
              }}
            >
              📋 Copy Link
            </button>
          </div>
          <div className="info-card" style={{ 
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            border: '1px solid rgba(0, 0, 0, 0.06)',
            borderRadius: '16px',
            padding: '1.5rem',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)'
          }}>
            <div className="info-card-icon" style={{ 
              fontSize: '2rem',
              width: '60px',
              height: '60px',
              background: 'linear-gradient(135deg, #30d158 0%, #28a745 100%)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              marginBottom: '1rem'
            }}>📊</div>
            <div className="info-card-content">
              <h4 className="info-card-title" style={{ 
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#64748b',
                margin: '0 0 0.5rem 0',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>Cafe Status</h4>
              <p className="info-card-value" style={{ 
                fontSize: '1.125rem',
                fontWeight: '600',
                color: '#30d158',
                margin: '0 0 0.5rem 0'
              }}>
                {isDemo ? 'Demo Active' : 'Active'}
              </p>
              <div className="info-card-detail" style={{ 
                fontSize: '0.875rem',
                color: '#64748b',
                lineHeight: '1.4'
              }}>
                {isDemo ? 'Demo mode with mock data' : 'All systems operational'}
              </div>
            </div>
          </div>
          <div className="info-card" style={{ 
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            border: '1px solid rgba(0, 0, 0, 0.06)',
            borderRadius: '16px',
            padding: '1.5rem',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)'
          }}>
            <div className="info-card-icon" style={{ 
              fontSize: '2rem',
              width: '60px',
              height: '60px',
              background: 'linear-gradient(135deg, #ff9f0a 0%, #ffad33 100%)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              marginBottom: '1rem'
            }}>👑</div>
            <div className="info-card-content">
              <h4 className="info-card-title" style={{ 
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#64748b',
                margin: '0 0 0.5rem 0',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>Your Role</h4>
              <p className="info-card-value" style={{ 
                fontSize: '1.125rem',
                fontWeight: '600',
                color: '#1d1d1f',
                margin: '0 0 0.5rem 0'
              }}>
                {isDemo ? 'Demo User' : (currentUser ? 'Owner' : 'Public User')}
              </p>
              <div className="info-card-detail" style={{ 
                fontSize: '0.875rem',
                color: '#64748b',
                lineHeight: '1.4'
              }}>
                {isDemo ? 'Full demo access' : (currentUser ? 'Full management access' : 'Public access only')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Mobile Quick Actions */}
      <div className="mobile-quick-actions">
        <h3 className="mobile-section-title">Quick Actions</h3>
        <div className="mobile-actions-grid">
          <Link to={`/${cafeSlug}/public`} className="mobile-action-btn">
            <span className="mobile-action-icon">🌐</span>
            <span className="mobile-action-text">Public Menu</span>
          </Link>
          <Link to={`/${cafeSlug}/order`} className="mobile-action-btn">
            <span className="mobile-action-icon">📱</span>
            <span className="mobile-action-text">Customer Orders</span>
          </Link>
          {(currentUser || isDemo) && (
            <>
              <Link to={`/${cafeSlug}/kitchen`} className="mobile-action-btn">
                <span className="mobile-action-icon">👨‍🍳</span>
                <span className="mobile-action-text">Kitchen</span>
              </Link>
              <Link to={`/${cafeSlug}/menu`} className="mobile-action-btn">
                <span className="mobile-action-icon">📋</span>
                <span className="mobile-action-text">Menu</span>
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShopDashboard; 